import { useCallback, useMemo, useState } from 'react';
import { axios, baseUrl, getToken } from 'Api';

// The shareable "become a viewer" link behind the Copy Invite Link button on
// the Viewers page. Kept here so every surface that offers the copy action
// (Viewers header, dashboard Viewers card) shares one link source and one copy
// implementation instead of drifting apart.
//
// The public id is fetched lazily on first copy (not on mount) so the Viewers
// card / page don't pay for a getPublicId round-trip on every load. Until then
// inviteLink falls back to the cached `code`.
//
// copyInviteLink resolves true/false rather than showing a toast, because each
// caller owns its own snackbar.
export const useInviteLink = () => {
  const [publicId, setPublicId] = useState('');

  const fetchPublicId = useCallback(async () => {
    try {
      const response = await axios.post(
        `${baseUrl}/getPublicId`,
        { token: getToken() },
        { headers: { 'Content-Type': 'application/json' } }
      );
      const id = response.data?.data?.id;
      if (id) setPublicId(id);
      return id || null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch public ID:', error);
      return null;
    }
  }, []);

  const buildLink = useCallback(
    (id) => {
      const { origin } = window.location;
      const inviteId = id || publicId || localStorage.getItem('code') || 'a1b2c3d4';
      return `${origin}/viewer/invite/${inviteId}`;
    },
    [publicId]
  );

  const inviteLink = useMemo(() => buildLink(), [buildLink]);

  const writeToClipboard = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for non-secure contexts / older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Copy link failed:', error);
      return false;
    }
  };

  const copyInviteLink = async () => {
    // Lazy-fetch the id on first copy so the link is accurate without a mount cost.
    let id = publicId;
    if (!id) id = await fetchPublicId();
    return writeToClipboard(buildLink(id));
  };

  return { inviteLink, copyInviteLink };
};

export default useInviteLink;
